import {
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format as formatDate,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { getScheduleViewDateRange } from './schedulePlannedDateWindow';

/** Matches {@link ScheduleTimelineScale} on the schedule page. */
export type ScheduleGanttScalePreset = 'today' | 'weeks' | 'months';

/**
 * Timeline row configuration for the Gantt `scales` prop (same shapes as used in Task/Resource charts).
 */
function scalesConfigForPreset(preset: ScheduleGanttScalePreset) {
  if (preset === 'today') {
    return [
      { unit: 'month' as const, step: 1, format: '%F %Y' },
      { unit: 'day' as const, step: 1, format: (d: Date): string => formatDate(d, 'EEE yyyy-MM-dd') },
    ];
  }
  if (preset === 'months') {
    return [
      { unit: 'year' as const, step: 1, format: '%Y' },
      { unit: 'month' as const, step: 1, format: '%F' },
    ];
  }
  return [
    { unit: 'month' as const, step: 1, format: '%F %Y' },
    { unit: 'week' as const, step: 1, format: (start: Date): string => formatDate(start, 'EEE yyyy-MM-dd') },
  ];
}

/**
 * First day of the week as the Gantt's week columns count it. SVAR's English locale starts weeks
 * on Sunday (`weekStart: 0`), so the week ticks are Sunday-based and the range must be too.
 */
export const SCHEDULE_GANTT_WEEK_STARTS_ON = 0 as const;

/**
 * Snap a range outwards to whole timeline cells (W-M13). The Gantt draws week (or month) columns
 * from the cell boundary, so a range starting Thu 13 Aug showed a first column headed "Sun 09 Aug"
 * - the header and the range disagreed. Starting the range on the boundary makes them agree.
 */
export function alignRangeToScale(
  from: Date,
  to: Date,
  preset: ScheduleGanttScalePreset
): { from: Date; to: Date } {
  if (preset === 'weeks') {
    return {
      from: startOfWeek(from, { weekStartsOn: SCHEDULE_GANTT_WEEK_STARTS_ON }),
      to: startOfDay(endOfWeek(to, { weekStartsOn: SCHEDULE_GANTT_WEEK_STARTS_ON })),
    };
  }
  if (preset === 'months') {
    return { from: startOfMonth(from), to: startOfDay(endOfMonth(to)) };
  }
  return { from: startOfDay(from), to: startOfDay(to) };
}

/**
 * Visible date span + scale granularity for schedule Gantt charts.
 *
 * - When start/end filters define a window ({@link getScheduleViewDateRange}), **dateFrom/dateTo** follow
 *   those filters; **Today / Weeks / Months** only changes timeline zoom (day vs week vs month ticks).
 * - When no filter dates, rolling windows around “now” match the previous behaviour.
 */
export function computeScheduleGanttTimeline(
  startDate: string,
  endDate: string,
  preset: ScheduleGanttScalePreset
): { dateFrom: string; dateTo: string; scalesConfig: ReturnType<typeof scalesConfigForPreset> } {
  const planned = getScheduleViewDateRange(startDate, endDate);
  if (planned) {
    const aligned = alignRangeToScale(planned.from, planned.to, preset);
    return {
      dateFrom: formatDate(aligned.from, 'yyyy-MM-dd'),
      dateTo: formatDate(aligned.to, 'yyyy-MM-dd'),
      scalesConfig: scalesConfigForPreset(preset),
    };
  }

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: SCHEDULE_GANTT_WEEK_STARTS_ON });

  if (preset === 'today') {
    const from = subWeeks(weekStart, 1);
    const to = addWeeks(weekStart, 3);
    return {
      dateFrom: formatDate(from, 'yyyy-MM-dd'),
      dateTo: formatDate(to, 'yyyy-MM-dd'),
      scalesConfig: scalesConfigForPreset('today'),
    };
  }
  if (preset === 'months') {
    const { from, to } = alignRangeToScale(subMonths(weekStart, 2), addMonths(weekStart, 8), 'months');
    return {
      dateFrom: formatDate(from, 'yyyy-MM-dd'),
      dateTo: formatDate(to, 'yyyy-MM-dd'),
      scalesConfig: scalesConfigForPreset('months'),
    };
  }

  const { from, to } = alignRangeToScale(subWeeks(weekStart, 4), addMonths(weekStart, 4), 'weeks');
  return {
    dateFrom: formatDate(from, 'yyyy-MM-dd'),
    dateTo: formatDate(to, 'yyyy-MM-dd'),
    scalesConfig: scalesConfigForPreset('weeks'),
  };
}
